import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Chip,
  Link
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

/**
 * Dialog component to display work items
 * @param {Object} props Component props
 * @param {boolean} props.open Whether the dialog is open
 * @param {Function} props.onClose Function to close the dialog
 * @param {Array} props.workItems Array of work items to display
 * @param {string} props.title Title of the dialog
 * @returns {JSX.Element} WorkItemsDialog component
 */
const WorkItemsDialog = ({ open, onClose, workItems = [], title }) => {
  // Function to get the ADO URL for a work item
  const getAdoUrl = (workItem) => {
    if (!workItem || !workItem.id) return '#';
    
    // Get the organization and project from the work item URL if available
    if (workItem.url) {
      return workItem.url;
    }
    
    // Use the specific URL format for HighQ Tech
    return `https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${workItem.id}`;
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      aria-labelledby="work-items-dialog-title"
    >
      <DialogTitle id="work-items-dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{title}</Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {workItems.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ py: 4 }}>
            No work items found.
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader aria-label="work items table">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>State</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workItems.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      {item.id === 'placeholder' ? (
                        <Typography color="textSecondary">N/A</Typography>
                      ) : (
                        <Link href={getAdoUrl(item)} target="_blank" rel="noopener noreferrer">
                          {item.id}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell>{item.title}</TableCell>
                    <TableCell>
                      <Chip 
                        label={item.workItemType || 'Unknown'} 
                        size="small"
                        color={
                          item.workItemType === 'Epic' ? 'primary' :
                          item.workItemType === 'Feature' ? 'info' :
                          item.workItemType === 'Product Backlog Item' ? 'success' :
                          item.workItemType === 'Bug' ? 'error' :
                          'default'
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.state || 'Unknown'} 
                        size="small"
                        color={
                          item.state === 'New' ? 'info' :
                          item.state === 'Active' ? 'primary' :
                          item.state === 'Resolved' ? 'warning' :
                          item.state === 'Closed' || item.state === 'Done' ? 'success' :
                          'default'
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkItemsDialog;
